import { Component, OnDestroy, Renderer2, ViewChild, OnInit } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { TariffService } from '@accounts/user/services/tariff.service';
import { StationService } from '@accounts/user/services/station.service';
import { GasNetworkTariffModel, GasStationModel, OrderModel, TrailerImageModel, TrailerModel } from '@core/api';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { ActivatedRoute, Router } from '@angular/router';
import { TrailerService } from '@accounts/user/services/trailer.service';
import { UserService } from '@accounts/user/services/user.service';
import { Observable, of, Subscription } from 'rxjs';
import { TransportModel } from '@core/api/model/transportModel';
import { GalleryItem, ImageItem } from '@ngx-gallery/core';
import { MatDialog, MatSnackBar, MatSnackBarConfig, MatSnackBarRef, SimpleSnackBar } from '@angular/material';
import { RegisteredService } from '@core/auth/services/registered.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { finalize, map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { UserService as AuthUserService } from '@core/auth';
import { filterReturnStations, resolveOriginStation } from '@core/utils/return-stations.helper';

@Component({
  selector: 'user-new-deal',
  templateUrl: './user-new-deal.component.html',
  styleUrls: ['./user-new-deal.component.scss'],
})
export class UserNewDealComponent implements OnInit, OnDestroy {
  @ViewChild('returnStationSelect') returnStationSelect: MatSelect;

  loading: boolean = false;
  createLoading: boolean = false;

  startDate: string = null;
  endDate: string = null;
  transport_id: number = null;

  galleryImages: GalleryItem[] = [];
  transport$: Subscription;
  currentStationById$: Subscription;
  isReserveDone: boolean = false;

  registeredSnackBar: MatSnackBarRef<SimpleSnackBar>;
  registeredService$: Subscription;

  isSelectingReturnStation: boolean = false;
  returnStationsWithAvailableSpaces: GasStationModel[] = [];
  selectedReturnStation: GasStationModel;
  returnPriceToAnotherPoint: number = 0;
  basePrice: number = 0;
  totalPrice: number = 0;

  isDailyTariff: boolean = false;
  showTariffDaysError: boolean = false;

  form: FormGroup;

  constructor(
      private tariffService: TariffService,
      protected stationService: StationService,
      private trailerService: TrailerService,
      private userService: UserService,
      private route: ActivatedRoute,
      private i18n: I18n,
      private registeredService: RegisteredService,
      private snackBar: MatSnackBar,
      private ren: Renderer2,
      private router: Router,
      private authUserService: AuthUserService,
      private fb: FormBuilder
  ) {
    this.form = this.fb.group({
      return_gas_station_id: [null],
      rent_days: [null],
    });

    this.form.get('rent_days').valueChanges.subscribe(() => {
      this.updatePriceSummary();
    });

    this.reinitPreOrderDate();
    this.userService.getTransports();

    this.transport$ = this.userService.transports.subscribe((data) => {
      if (data[0]) {
        this.transport_id = data[0].id;
      }
    });

    this.registeredService$ = this.registeredService.localStorage.subscribe((isRegistered) => {
      if (isRegistered) {
        this.openSnackBar();
      }
    });

  }

  ngOnInit() {
  }

  private initReturnStationsSelector() {
    const trailerId = Number(this.route.snapshot.paramMap.get('trailerId'));
    const returnStationId = Number(this.route.snapshot.paramMap.get('returnStationId'));
    const rentalStation = this.stationService.station;
    const trailer = this.tariffService.trailer;
    const originId = trailer && trailer.origin_gas_station_id
      ? Number(trailer.origin_gas_station_id)
      : null;
    const originFromTrailer = trailer && trailer.originGasStation;
    const nonTransit = !!(trailer && trailer.non_transit);

    this.stationService.getGasStationsWithTransitSpaces(rentalStation.network_id, trailerId).pipe(
      catchError(() => of({ success: true, data: [] as GasStationModel[] })),
      switchMap((data) => {
        const stations = (data && data.success && data.data) ? data.data.slice() : [];

        if (!stations.length && trailerId && !nonTransit) {
          return this.stationService.getGasStationsWithTransitSpaces(rentalStation.network_id).pipe(
            map(fallback => (fallback && fallback.success && fallback.data) ? fallback.data.slice() : []),
            catchError(() => of([] as GasStationModel[]))
          );
        }

        return of(stations);
      }),
      switchMap((stations) => {
        let originStation = resolveOriginStation(originId, originFromTrailer, stations);

        if (originId && originId !== Number(rentalStation.id) && (!originStation || !originStation.location_group)) {
          return this.stationService.getStationById(originId).pipe(
            map(fetched => {
              if (originFromTrailer && originFromTrailer.location_group && !fetched.location_group) {
                return Object.assign({}, fetched, { location_group: originFromTrailer.location_group });
              }
              return fetched;
            }),
            map(origin => ({ stations, originStation: origin })),
            catchError(() => of({ stations, originStation }))
          );
        }

        return of({ stations, originStation });
      })
    ).subscribe(
      (result) => {
        this.returnStationsWithAvailableSpaces = filterReturnStations(
          result.stations,
          rentalStation,
          result.originStation,
          nonTransit
        );

        if (returnStationId) {
          this.selectedReturnStation = this.returnStationsWithAvailableSpaces.find(
            (station) => Number(station.id) === Number(returnStationId)
          );
        }

        if (!this.selectedReturnStation) {
          this.selectedReturnStation = this.returnStationsWithAvailableSpaces.find(
            (station) => Number(station.id) === Number(rentalStation.id)
          );
        }

        const selectedId = this.selectedReturnStation ? this.selectedReturnStation.id : null;
        this.form.get('return_gas_station_id').setValue(selectedId);
        this.updatePriceSummary();
      },
      (error) => {
        console.error('Ошибка загрузки станций:', error);
      }
    );
  }

  openSnackBar() {
    const config: MatSnackBarConfig = {
      panelClass: 'mat-snack-registration-message',
      duration: 15 * 1000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    };
    this.registeredSnackBar = this.snackBar.open('Вы успешно зарегистрировались!', '', config);
    this.registeredSnackBar.afterDismissed().subscribe(() => {
      this.registeredService.reset();
    });

    let snackEl = document.getElementsByClassName('mat-snack-registration-message').item(0);
    if (snackEl) {
      this.ren.listen(snackEl, 'click', () => this.registeredSnackBar.dismiss());
    }
  }

  openSelectReturnStation() {
    this.isSelectingReturnStation = true;
    setTimeout(() => {
      if (this.returnStationSelect) {
        this.returnStationSelect.open();
      } else {
        console.error('returnStationSelect не определен');
      }
    }, 0);
  }

  onReturnStationSelected() {
    this.isSelectingReturnStation = false;
    this.selectedReturnStation = this.returnStationsWithAvailableSpaces.find(
        (station) => station.id === this.form.get('return_gas_station_id').value
    );
    this.updatePriceSummary();
  }

  get forbiddenCreateOrder(): boolean {
    return !this.transport_id;
  }

  get transports(): Observable<TransportModel[]> {
    return this.userService.transports;
  }

  get loadingTransports(): Observable<boolean> {
    return this.userService.loadingTransports;
  }

  get loading$() {
    return this.tariffService.tariffLoading$;
  }

  get stationAddress(): string {
    if (this.selectedReturnStation) {
      return this.selectedReturnStation.address;
    }

    return this.stationService.station.address;
  }

  get isGallery(): boolean {
    return !!this.tariffService.trailer.images.length;
  }

  get trailerName(): string {
    return `${this.tariffService.trailer.brand} ${this.tariffService.trailer.model}`
  }

  get trailerRegistrationNumber(): string {
    return this.tariffService.trailer.registration_number;
  }

  get trailerSpareWheel(): boolean {
    return this.tariffService.trailer.spare_wheel;
  }

  get trailerWithTent(): boolean {
    return this.tariffService.trailer.type === TrailerModel.TypeEnum.WithTent;
  }

  get trailerAdapter(): boolean {
    return this.tariffService.trailer.plug_adapter_seven_to_thirteen;
  }

  get trailerLoadCapacity(): number {
    return this.tariffService.trailer.load_capacity;
  }

  tariffs: GasNetworkTariffModel[] = [];
  get selectedTariff(): GasNetworkTariffModel {
    return this.tariffService.tariff;
  }

  private initGallery() {
    this.galleryImages = this.tariffService.trailer.images
      .sort((a: TrailerImageModel, b: TrailerImageModel) => Number(b.main) - Number(a.main))
      .map((image: TrailerImageModel) => {
        return new ImageItem({ src: image.url, thumb: image.url });
      });
  }

  private reinitPreOrderDate() {
    this.loading = true;

    const stationId = Number(this.route.snapshot.paramMap.get('stationId'));
    const trailerId = Number(this.route.snapshot.paramMap.get('trailerId'));
    const tariffId = Number(this.route.snapshot.paramMap.get('tariffId'));
    const rentDays = Number(this.route.snapshot.paramMap.get('rentDays'));

    this.currentStationById$ = this.stationService
        .getCurrentStationById(stationId)
        .pipe(
            switchMap(() => {
              return this.trailerService.getTrailerById(trailerId);
            })
        )
        .pipe(finalize(() => (this.loading = false)))
        .subscribe((trailer) => {
          this.tariffService.trailer = trailer;
          this.initGallery();
          this.initReturnStationsSelector();
          this.tariffs = this.stationService.station.tariffs.sort((a, b) => (a.min_rent_hours > b.min_rent_hours ? 1 : -1));
          if (tariffId) {
            this.tariffService.tariff = this.tariffs.find((tariff) => tariff.id === tariffId);
          } else {
            this.tariffService.tariff = this.tariffs[0];
          }

          let skipValueChange = false;
          if (rentDays) {
            skipValueChange = true;
            this.form.get('rent_days').setValue(rentDays);
          }

          this.selectTarif(this.tariffService.tariff, skipValueChange);
          this.updatePriceSummary();
        });
  }

  private timedateInit() {
    this.startDate = moment()
      .add(environment.registrationTimeout, 'minutes')
      .format(environment.dateTimeFormat);

    this.endDate = moment()
      .add(environment.registrationTimeout, 'minutes')
      .add(this.tariffService.tariffValue, 'hours')
      .format(environment.dateTimeFormat);
  }

  selectTarif(tariff: GasNetworkTariffModel, skipChangeValue = false) {
    this.tariffService.tariff = tariff;
    this.isDailyTariff = tariff.type === GasNetworkTariffModel.TypeEnum.Daily;
    if (this.isDailyTariff && !skipChangeValue) {
      switch (tariff.min_rent_hours) {
        case 24:
          this.form.get('rent_days').setValue(1);
          break;
        case 72:
          this.form.get('rent_days').setValue(3);
          break;
        case 168:
          this.form.get('rent_days').setValue(7);
          break;
      }
    }

    this.updatePriceSummary();
  }

  getOrderModel(): OrderModel {
    this.timedateInit();

    let rentHours = this.tariffService.tariff.type === GasNetworkTariffModel.TypeEnum.Daily
        ? this.form.get('rent_days').value * 24
        : this.tariffService.tariff.min_rent_hours;

    const rentDays = this.form.get('rent_days').value || 1;

    return {
      user_id: this.userService.user.id,
      gas_station_id: this.stationService.station.id,
      trailer_id: this.tariffService.trailer.id,
      tariff_id: this.tariffService.tariff.id as number,
      transport_id: this.transport_id,
      rent_hours: rentHours,
      tariff_type: this.tariffService.tariff.type,
      cost: Number((this.tariffService.tariff.cost * rentDays + this.returnPriceToAnotherPoint).toFixed(2)),
      ordered_to: this.endDate,
      has_transfer_act: null,
      has_return_act: null,
      trailer: this.tariffService.trailer,
      gasStation: this.stationService.station,
      returnGasStation: this.selectedReturnStation
    };
  }

  createOrder(isReserveDone: boolean) {
    if (this.tariffService.tariff.type === GasNetworkTariffModel.TypeEnum.Daily) {
      const selectedRentHours = this.form.get('rent_days').value * 24;

      switch (this.tariffService.tariff.min_rent_hours) {
        case 24:
          if (selectedRentHours < 24 || selectedRentHours >= 72) {
            this.showTariffDaysError = true;
            return;
          }
          break;
        case 72:
          if (selectedRentHours < 72 || selectedRentHours >= 168) {
            this.showTariffDaysError = true;
            return;
          }
          break;
        case 168:
          if (selectedRentHours < 168) {
            this.showTariffDaysError = true;
            return;
          }
          break;
      }
    }

    this.showTariffDaysError = false;

    if (!this.authUserService.loginIn) {
      let params = ['cabinet/new', this.stationService.station.id, this.tariffService.trailer.id, this.selectedReturnStation.id, this.tariffService.tariff.id];

      if (this.tariffService.tariff.type === GasNetworkTariffModel.TypeEnum.Daily) {
        params.push(this.form.get('rent_days').value);
      }

      localStorage.setItem(
          'guestAuthRedirect',
          JSON.stringify(params)
      );
      this.router.navigate(['/auth/login']);
    } else {
      this.isReserveDone = isReserveDone;
    }
  }

  isAnonymous(): boolean {
    return !this.authUserService.loginIn;
  }

  ngOnDestroy() {
    this.currentStationById$.unsubscribe();
    this.transport$.unsubscribe();
    this.registeredService$.unsubscribe();
    this.tariffService.trailer = null;
  }

  private updatePriceSummary() {
    const isOther = this.selectedReturnStation && this.stationService.station && this.selectedReturnStation.id !== this.stationService.station.id;
    const tariff = this.tariffService.tariff;

    if (!tariff) {
      this.basePrice = 0;
      this.returnPriceToAnotherPoint = 0;
      this.totalPrice = 0;
      
      return;
    }

    const rentDays = this.form.get('rent_days').value || 1;
    
    this.basePrice = Number(tariff.cost || 0) * rentDays;
    this.returnPriceToAnotherPoint = isOther ? Number(tariff.return_price_to_another_point || 0) : 0;
    this.totalPrice = this.basePrice + this.returnPriceToAnotherPoint;
  }

}